import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ActivityCategory } from '@wilson/interfaces';
import { TransformMasterDataTranslationService } from '@wilson/non-domain-specific/master-data-translate';
import { ActivityCategoriesState } from '../state/activity-categories.state';

export type ActivityCategoryReference =
  | string
  | { activityCategoryId: string }
  | { activityCategoryName: string }
  | { id: string }
  | ActivityCategory
  | null
  | undefined;

/**
 * Imperative counterpart of `TranslateActivityCategoryPipe` — call this from
 * TypeScript instead of injecting the pipe.
 * @see https://angular.dev/guide/templates/pipes#using-pipe-logic-outside-templates
 */
@Injectable({ providedIn: 'root' })
export class TranslateActivityCategoryService {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly transformMasterDataTranslationService = inject(
    TransformMasterDataTranslationService,
  );

  resolve(value: ActivityCategoryReference): string {
    if (!value) {
      return this.notAvailable();
    }

    if (typeof value === 'string') {
      return this.byId(value);
    }

    if ('nameEn' in value || 'isMain' in value) {
      return this.translateEntity(value as ActivityCategory);
    }

    if ('activityCategoryName' in value) {
      return this.byName(value.activityCategoryName);
    }

    if ('activityCategoryId' in value) {
      return this.byId(value.activityCategoryId);
    }

    if ('id' in value) {
      return this.byId(value.id);
    }

    return this.notAvailable();
  }

  private byId(id: string): string {
    if (!id) {
      return this.notAvailable();
    }

    const category = this.store.selectSnapshot(
      ActivityCategoriesState.categoryById(id),
    );
    return category ? this.translateEntity(category) : this.notAvailable();
  }

  private byName(name: string): string {
    if (!name) {
      return this.notAvailable();
    }

    const category = this.store.selectSnapshot(
      ActivityCategoriesState.categoryByName(name),
    );
    return category ? this.translateEntity(category) : this.notAvailable();
  }

  private translateEntity(category: ActivityCategory): string {
    try {
      return (
        this.transformMasterDataTranslationService.transform([category])[0]
          .translatedName || this.notAvailable()
      );
    } catch {
      return this.notAvailable();
    }
  }

  private notAvailable(): string {
    return this.translateService.instant('general.n/a');
  }
}
